import type { AdPurchaseStatus } from '../../api';

// Köp av annons (P1): texter och det sparade köpet som överlever inloggningen
// (även Qid-omdirigeringen, som landar på / i stället för annonsen).

export const PENDING_PURCHASE_KEY = 'qvenari_pending_purchase';
/** Ett sparat köp äldre än så här återupptas inte. */
const PENDING_TTL_MS = 60 * 60_000;

export interface PendingPurchase { adId: string; dateStart: string | null; at: number }

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
const store = (): Store | null => { try { return sessionStorage; } catch { return null; } };

export function savePendingPurchase(adId: string, dateStart: string | null, s: Store | null = store(), now = Date.now()): void {
  try { s?.setItem(PENDING_PURCHASE_KEY, JSON.stringify({ adId, dateStart, at: now })); } catch { /* privat läge */ }
}

/** Läser det sparade köpet utan att ta bort det (för att hitta tillbaka till annonsen). */
export function peekPendingPurchase(s: Store | null = store(), now = Date.now()): PendingPurchase | null {
  try {
    const raw = s?.getItem(PENDING_PURCHASE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as PendingPurchase;
    if (!p?.adId || typeof p.at !== 'number' || now - p.at > PENDING_TTL_MS) { s?.removeItem(PENDING_PURCHASE_KEY); return null; }
    return { adId: String(p.adId), dateStart: p.dateStart ?? null, at: p.at };
  } catch { return null; }
}

/** Tar det sparade köpet om det gäller annonsen (en gång). */
export function takePendingPurchase(adId: string, s: Store | null = store(), now = Date.now()): PendingPurchase | null {
  const p = peekPendingPurchase(s, now);
  if (!p || p.adId !== adId) return null;
  try { s?.removeItem(PENDING_PURCHASE_KEY); } catch { /* ignore */ }
  return p;
}

export const ACTIVE_PURCHASE: AdPurchaseStatus[] = ['checkout', 'reserved', 'approving', 'approved'];

/** Status som jägaren ser i "Mina köp" och på annonsen. free: ansökan utan kort (D5, belopp 0). */
export function purchaseStatusText(status: AdPurchaseStatus, note?: string | null, free = false): { label: string; detail: string; tone: 'wait' | 'ok' | 'off' | 'bad' } {
  if (free) {
    switch (status) {
      case 'reserved':
      case 'approving': return { label: 'Ansökan skickad', detail: 'Marken går igenom din ansökan. Inget kort behövs.', tone: 'wait' };
      case 'approved': return { label: 'Godkänd', detail: 'Du får ett avtal att signera.', tone: 'ok' };
      case 'rejected': return { label: 'Inte godkänd', detail: note || 'Marken tackade nej den här gången.', tone: 'off' };
      case 'released_timeout': return { label: 'Ansökan gick ut', detail: 'Ingen beslutade i tid – du kan ansöka igen.', tone: 'off' };
      default: break;
    }
  }
  switch (status) {
    case 'checkout': return { label: 'Betalning pågår', detail: 'Betalningen är inte klar.', tone: 'wait' };
    case 'reserved':
    case 'approving': return { label: 'Väntar på godkännande', detail: 'Beloppet är reserverat på ditt kort men inte draget.', tone: 'wait' };
    case 'approved': return { label: 'Godkänd', detail: 'Betalningen är dragen. Du får ett avtal att signera.', tone: 'ok' };
    case 'rejected': return { label: 'Inte godkänd', detail: `Reservationen är släppt och inget har dragits.${note ? ` ${note}` : ''}`, tone: 'off' };
    case 'released_timeout': return { label: 'Reservationen släpptes', detail: note || 'Ingen beslutade i tid – reservationen släpptes och inget drogs.', tone: 'off' };
    case 'cancelled': return { label: 'Avbrutet', detail: 'Köpet slutfördes inte. Inget har dragits.', tone: 'off' };
    case 'capture_failed': return { label: 'Betalningen misslyckades', detail: note || 'Reservationen gick inte att dra. Inget har dragits – köp igen om du vill.', tone: 'bad' };
  }
}

/** Ansökan utan kort (D5): jaktlag och gratis grupper har belopp 0. */
export const isFreePurchase = (p: { amount: number | null | undefined }) => p.amount === 0;

/** Varför köpknappen är spärrad. */
export function blockedText(reason: string | null | undefined): string {
  switch (reason) {
    case 'owner_not_connected': return 'Markägaren har inte aktiverat kortbetalning ännu.';
    case 'full': return 'Inga platser kvar.';
    case 'apply_only': return 'Jaktlag köps inte – ansök i stället.';
    case 'no_price': return 'Annonsen saknar pris.';
    case 'not_published': return 'Annonsen är inte publicerad.';
    case 'already_bought': return 'Du har redan köpt den här.';
    case 'date_passed': return 'Datumet har redan passerat.';
    default: return 'Går inte att köpa just nu.';
  }
}

/** Skickas av App.tsx när ett köp bekräftats efter retur från Stripe, så att vyerna hämtar om. */
export const PURCHASE_UPDATED_EVENT = 'qvenari:ad-purchase-updated';

export const spotsText = (n: number) => (n === 0 ? 'Inga platser kvar' : n === 1 ? '1 plats kvar' : `${n} platser kvar`);

export const kronor = (n: number) => `${n.toLocaleString('sv-SE', { maximumFractionDigits: 2 })} kr`;

/** D5: markens publicerade annonser (en mark kan ha flera; jaktpaket räknas om marken ingår). */
export function adsForMark<A extends { markId: string; status: string; lines?: { markId: string }[] | null }>(ads: A[], markId: string): A[] {
  return ads.filter(a => a.status === 'published' && (a.markId === markId || !!a.lines?.some(l => l.markId === markId)));
}
